import Combobox from '@/components/combobox/Combobox';
import ComponentGuard from '@/components/component-guard/ComponentGuard';
import CustomButton from '@/components/custom-button/custom-btn.component';
import ExportReportButton from '@/components/ExportReportButton';
import PageLayout from '@/components/PageLayout';
import PrintOutHead from '@/components/PrintOutHead';
import { PrintView } from '@/components/PrintView/print-view.component';
import { RenderIf } from '@/components/RenderIf';
import { formatDate, getItemById, getQueryString } from '@/utils/common.util';
import { useDebounceState } from '@/utils/useDebounceState';
import useCurrentAcademicTerm from '@/utils/useCurrentAcademicTerm';
import { MAIN_ROLES } from '@/utils/user-roles';
import { useUrlState } from '@/utils/useUrlState';
import {
  DatePicker,
  DatePickerInput,
  Pagination,
  Select,
  SelectItem,
} from 'carbon-components-react';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Printer } from 'react-feather';
import useSWR from 'swr';

const initialData = {
  classId: '',
  fromDate: '',
  toDate: '',
  academicYearId: '',
  termId: '',
  studentId: '',
  classDescription: '',
};

export default function StudentAttendanceSummary() {
  const pageTitle = 'Student Attendance Summary Report';
  const [query, setQuery] = useUrlState(initialData);

  useCurrentAcademicTerm({
    academicYearId: query.academicYearId,
    setQuery,
  });

  const { data: academicYears } = useSWR(
    'academic-year/list/?skip=0&take=100&query=',
  );
  const { data: terms } = useSWR(
    query.academicYearId
      ? `academic-year/terms/?academicYearId=${query.academicYearId}`
      : null,
  );
  const { data: classes } = useSWR('class/list');

  useEffect(() => {
    if (query.classDescription && !query.classId && classes?.length) {
      const matchedClass = classes.find(
        (c) => c.description === query.classDescription,
      );
      if (matchedClass) {
        setQuery({ classId: matchedClass.id.toString() });
      }
    }
  }, [query.classDescription, query.classId, classes, setQuery]);

  const [searchStudent, setSearchStudent] = useDebounceState('');
  const { data: students, isLoading: isStudentsLoading } = useSWR(
    `student/list?status=Enrolled&${getQueryString({
      query: searchStudent,
      classId: query.classId,
    })}`,
  );

  const [paginate, setPaginate] = useState({
    page: 1,
    pageSize: 20,
  });

  const mainUrl = 'students-attendance/reports/student-summary';
  const { data, isLoading, error } = useSWR(
    `${mainUrl}?${getQueryString({
      ...query,
      take: paginate.pageSize,
      skip: paginate.page - 1,
    })}`,
  );

  const attendanceData = data?.data ?? [];
  const totalCount = data?.pagingInfo?.recordsCount || 0;
  const totals = data?.extendedData || {};
  const totalExpectedDays = totals?.totalExpectedDays || 0;
  const startDate = formatDate(totals?.fromDate);
  const endDate = formatDate(totals?.toDate);

  return (
    <PageLayout
      breadcrumbs={[
        { label: 'Academics', to: '/academic' },
        { label: 'Attendance', to: '/students/attendance' },
        { label: pageTitle, active: true },
      ]}
      title={pageTitle}
      isLoading={isLoading}
    >
      {/* FILTERS  */}
      <p>Select academic year to display report</p>
      <div className="mt-10 card-row-3 gap-20">
        <ComponentGuard
          allowedRoles={[
            MAIN_ROLES.ACADEMIC_YEAR,
            MAIN_ROLES.ACADEMIC_YEAR_READ_ONLY,
          ]}
        >
          <Select
            labelText="Academic Year"
            onChange={(e) =>
              setQuery({ academicYearId: e.target.value, termId: '' })
            }
            value={query.academicYearId}
          >
            <SelectItem value="" text="All" />
            {academicYears?.map((year) => (
              <SelectItem
                key={year.id}
                value={year.id}
                text={year.description}
              />
            ))}
          </Select>
        </ComponentGuard>

        <Select
          labelText="Term"
          disabled={!query.academicYearId}
          onChange={(e) => setQuery({ termId: e.target.value })}
          value={query.termId}
        >
          <SelectItem value="" text="All" />
          {terms?.map((term) => (
            <SelectItem key={term.id} value={term.id} text={term.description} />
          ))}
        </Select>

        <DatePicker
          dateFormat="Y-m-d"
          datePickerType="single"
          id="start-date-picker"
          maxDate={new Date().toISOString().split('T')[0]}
          onChange={(value) => {
            if (value.length > 0) {
              setQuery({
                fromDate: [
                  value[0].getFullYear(),
                  ('0' + (value[0].getMonth() + 1)).slice(-2),
                  ('0' + value[0].getDate()).slice(-2),
                ].join('-'),
              });
            } else setQuery({ fromDate: '' });
          }}
          onClose={() => {}}
        >
          <DatePickerInput
            className="start-date"
            id="start-date"
            name="fromDate"
            labelText="Start Date"
            value={query.fromDate}
            max={new Date().toISOString().split('T')[0]}
            pattern="\\d{4}-\\d{2}-\\d{2}"
            autoComplete="off"
            placeholder="YYYY-MM-DD"
            type="text"
            onChange={(e) => setQuery({ fromDate: e.target.value })}
          />
        </DatePicker>

        <DatePicker
          dateFormat="Y-m-d"
          datePickerType="single"
          id="end-date-picker"
          maxDate={new Date().toISOString().split('T')[0]}
          onChange={(value) => {
            if (value.length > 0) {
              setQuery({
                toDate: [
                  value[0].getFullYear(),
                  ('0' + (value[0].getMonth() + 1)).slice(-2),
                  ('0' + value[0].getDate()).slice(-2),
                ].join('-'),
              });
            } else setQuery({ toDate: '' });
          }}
          onClose={() => {}}
        >
          <DatePickerInput
            className="end-date"
            id="end-date"
            name="toDate"
            labelText="End Date"
            value={query.toDate}
            max={new Date().toISOString().split('T')[0]}
            pattern="\\d{4}-\\d{2}-\\d{2}"
            autoComplete="off"
            placeholder="YYYY-MM-DD"
            type="text"
            onChange={(e) => setQuery({ toDate: e.target.value })}
          />
        </DatePicker>

        <Select
          labelText="Class"
          onChange={(e) => setQuery({ classId: e.target.value, studentId: '' })}
          value={query.classId}
        >
          <SelectItem value="" text="All" />
          {classes?.map((term) => (
            <SelectItem key={term.id} value={term.id} text={term.description} />
          ))}
        </Select>

        <Combobox
          options={students ?? []}
          label="Select a Student"
          value={
            query.studentId
              ? students?.find((s) => s.id === Number(query.studentId))
              : null
          }
          onChange={(option) => {
            setQuery({
              studentId: option ? option.id : '',
              // searchQuery: option ? option.firstName : '',
            });
            setSearchStudent('');
          }}
          onSearch={setSearchStudent}
          onClear={() => {
            setQuery({ studentId: '' });
            setSearchStudent('');
          }}
          loading={isStudentsLoading}
          renderSelected={(option) => `${option.firstName} ${option.lastName}`}
          renderOption={(option) => `${option.firstName} ${option.lastName}`}
          labelKey="fullName"
          valueKey="id"
          searchPlaceholder="Search students..."
          placeholder=""
        />
      </div>

      <div>
        {error && <p className="text-14 mt-10 text-danger">{error.message}</p>}
        {attendanceData?.length === 0 && (
          <p className="text-14 mt-30 text-grey">
            No attendance records found for the selected filters.
          </p>
        )}

        <div className="mt-30 d-flex gap-20 justify-between align-items-center">
          <RenderIf condition={totals?.fromDate}>
            <p>
              Date: <span className="font-bold">{startDate}</span> -{' '}
              <span className="font-bold">{endDate}</span>{' '}
            </p>
          </RenderIf>
          <RenderIf condition={totalExpectedDays}>
            <p>
              Total Expected Days:{' '}
              <span className="font-bold">{totalExpectedDays}</span>
            </p>
          </RenderIf>
        </div>

        <RenderIf condition={attendanceData?.length > 0}>
          <PrintOutHead
            title={pageTitle}
            headings={[
              {
                label: 'Academic Year',
                value:
                  getItemById(academicYears, query.academicYearId)
                    ?.description ?? 'All',
              },
              {
                label: 'Term',
                value: getItemById(terms, query.termId)?.description ?? 'All',
              },
              {
                label: 'Date Range',
                value: totals?.fromDate ? `${startDate} - ${endDate} ` : '-',
              },
              {
                label: 'Class',
                value:
                  getItemById(classes, query.classId)?.description ?? 'All',
              },
              {
                label: 'Student',
                value: query.studentId
                  ? students?.find((s) => s.id === query.studentId)?.fullName
                  : 'All',
              },
              {
                label: 'Total Expected Days',
                value: totalExpectedDays,
              },
            ]}
          />

          <PrintView>
            <div style={{ overflow: 'auto' }}>
              <table className="bx--data-table table-white">
                <thead>
                  <tr>
                    <th>Student ID</th>
                    <th>Name</th>
                    <th>Class</th>
                    <th>Present</th>
                    <th>Absent</th>
                    <th>Late</th>
                    <th>Excused</th>
                    <th>Total</th>
                    <th className="no-print">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {attendanceData?.map((entry, idx) => (
                    <tr key={entry.id ?? idx}>
                      <td>{entry.studentNumber}</td>
                      <td>{entry.name}</td>
                      <td>{entry.className}</td>
                      <td>{entry.present}</td>
                      <td>{entry.absent}</td>
                      <td>{entry.late}</td>
                      <td>{entry.excused}</td>
                      <td>{entry.total}</td>
                      <td className="no-print">
                        <Link
                          to={`/analytics/students-attendance-report?${getQueryString(
                            {
                              ...query,
                              studentId: entry.studentId,
                              classDescription: entry.className,
                            },
                          )}`}
                          className="link"
                        >
                          View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="font-bold">
                    <td>Total</td>
                    <td colSpan={2}>{totals.totalStudents}</td>
                    <td>{totals.present}</td>
                    <td>{totals.absent}</td>
                    <td>{totals.late}</td>
                    <td>{totals.excused}</td>
                    <td colSpan={2}>{totals.total}</td>
                  </tr>
                </tfoot>
              </table>
              <div className="no-print">
                <Pagination
                  backwardText="Previous page"
                  forwardText="Next page"
                  page={paginate.page}
                  pageNumberText="Page Number"
                  pageSize={paginate.pageSize}
                  pageSizes={[10, 20, 30, 40, 50, 100]}
                  totalItems={totalCount}
                  onChange={(value) => setPaginate(value)}
                />
              </div>
            </div>
            <RenderIf
              condition={totalExpectedDays && totals?.expectedDaysBasis}
            >
              <p className="text-12 mt-10 text-grey">
                {totals.expectedDaysBasis === 'TermTotalSchoolDays'
                  ? 'Total Expected Days uses the admin-set Total School Days for the selected term.'
                  : 'Total Expected Days counts distinct school days on which attendance was actually recorded for the selected classes in this period.'}
              </p>
            </RenderIf>
          </PrintView>
        </RenderIf>

        <RenderIf condition={attendanceData?.length > 0}>
          <div className="mt-30 d-flex gap-20 align-items-center flex-end">
            <ExportReportButton
              apiUrl={`${mainUrl}/export?${getQueryString(query)}`}
              fileName={`${pageTitle}.xlsx`}
            />
            <CustomButton classes="secondary" onClick={() => window.print()}>
              <Printer size="16" strokeWidth="2px" /> &nbsp; Print
            </CustomButton>
          </div>
        </RenderIf>
      </div>
    </PageLayout>
  );
}
